import { useCustomerStore } from "../stores/CustomerStore";
import { usePriceHelper } from "./PriceHelper";
import { collect } from "collect.js";
import { defineStore } from "pinia";
import { BasketEntry, BasketPayload, Order, OrderRow, Product, UpsellBasketPayload } from "@daalder/types";
import { type CountryCode, parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
import { sha256 } from "js-sha256";
import { useDiscountHelper } from "./DiscountHelper";
import DayJs from "./DayJs";

const { getPrice, getLowestPriceAsObject } = usePriceHelper();
const { getUnitPriceExclDiscounts, getUnitPriceInclDiscounts } = useDiscountHelper();

export function useGA4() {
    let customerStore: Omit<ReturnType<typeof useCustomerStore>, keyof ReturnType<typeof defineStore>> = null;

    function clearPreviousEcommerceObject() {
        window.dataLayer.push({ ecommerce: null, customer_data: null });
    }

    function clearPreviousValidationErrorObject() {
        window.dataLayer.push({
            ecommerce: null,
            error_fields: null,
            error_fields_messages: null,
            error_message: null,
        });
    }

    function getDiscount(prices: any, amount: number = 1): string {
        let unitPriceExclDiscounts: number | null = getUnitPriceExclDiscounts(prices);
        let unitPriceInclDiscounts: number | null = getUnitPriceInclDiscounts(prices, amount);

        if (!!unitPriceExclDiscounts && !!unitPriceInclDiscounts) {
            return (unitPriceExclDiscounts - unitPriceInclDiscounts).toFixed(2);
        }

        return "0.00";
    }

    function mapProductForGA(
        product: Product,
        listName: string = null,
        listIndex: number = null,
        quantity: number = 1,
    ) {
        let mappedProduct: {
            item_name: string;
            item_id: string;
            price: string;
            discount: string;
            currency: string;
            item_brand?: string;
            item_category?: string;
            item_list_name?: string;
            index?: number;
            quantity: number;
        } = {
            item_name: product.name,
            item_id: `${product.id}`,
            price: getUnitPriceExclDiscounts(product.prices)?.toFixed(2),
            discount: getDiscount(product.prices, quantity),
            currency: getLowestPriceAsObject(product.prices, quantity)?.currency.code,
            quantity: quantity,
        };

        if (product.brand) {
            mappedProduct.item_brand = product.brand.name;
        }
        if (product.group) {
            mappedProduct.item_category = product.group.name;
        }
        if (listName) {
            mappedProduct.item_list_name = listName;
        }
        if (listIndex) {
            mappedProduct.index = listIndex;
        }

        return mappedProduct;
    }

    function mapProductsForGA(
        productsData: { amount: number; product: Product }[],
        listName: string = null,
        listIndex: number = null,
    ) {
        return productsData.map((productData) => {
            return mapProductForGA(productData.product, listName, listIndex, productData.amount);
        });
    }

    function mapProductsDataToValue(productsData: { amount: number; product: Product }[]): string {
        const value = collect(productsData).sum((productData) => {
            return getUnitPriceInclDiscounts(productData.product.prices, productData.amount) * productData.amount;
        });

        return (<number>value).toFixed(2);
    }

    function mapProductsDataToTax(productsData: { amount: number; product: Product }[]) {
        const combinedPrice = collect(productsData).sum((productData) => {
            return getPrice(productData.product.prices, productData.amount) * productData.amount;
        });

        const combinedPriceExclVat = collect(productsData).sum((productData) => {
            let priceExcludingVat =
                getLowestPriceAsObject(productData.product.prices, productData.amount)?.price_excluding_vat ?? 0;
            return +priceExcludingVat * productData.amount;
        });

        return parseFloat((+combinedPrice - +combinedPriceExclVat).toFixed(2));
    }

    function mapProductsDataToCurrency(productsData: { amount: number; product: Product }[]) {
        return getLowestPriceAsObject(productsData[0]?.product?.prices, productsData[0]?.amount)?.currency?.code;
    }

    function mapOrderToGAProductsData(order: Order): { amount: number; product: Product }[] {
        return collect(order.order_rows)
            .whereNotNull("product")
            .map((orderRow: OrderRow) => {
                return {
                    amount: orderRow.amount,
                    product: orderRow.product,
                };
            });
    }

    function mapBasketEntriesToGAProductsData(entries: BasketEntry[]) {
        // @ts-ignore TODO: REMOVE THIS TS-IGNORE
        return entries.flatMap((entry) => {
            let mappedProducts = [
                {
                    amount: entry.amount,
                    product: entry.product,
                },
            ];

            if (entry?.product_parts) {
                entry.product_parts.forEach((productPart) => {
                    mappedProducts.push({
                        amount: productPart.amount,
                        product: productPart,
                    });
                });
            }

            if (entry?.upsell) {
                entry.upsell.products.forEach((upsellProduct) => {
                    mappedProducts.push({
                        amount: upsellProduct.amount,
                        product: upsellProduct,
                    });
                });
            }

            return mappedProducts;
        });
    }

    function mapCombiUpsellBasketPayloadToGAProductsData(
        upsellBasketPayload: UpsellBasketPayload,
        apiBasketEntries: BasketEntry[],
    ) {
        let apiEntry = collect(apiBasketEntries)
            .filter((apiBasketEntry: BasketEntry) => {
                return apiBasketEntry.product_id === upsellBasketPayload.product_id && !!apiBasketEntry.upsell;
            })
            .sortByDesc("id")
            .first();

        // Use the amount from the payload, not the response. If the API call was 'add 2 of this combi-upsell' and the
        // basket already contained 5 of the combi-upsell, the response would have an amount of 7. This makes sure
        // we log 2, not 7.
        let productData = [
            {
                amount: upsellBasketPayload.amount,
                product: apiEntry.product,
            },
        ];

        apiEntry.upsell?.products?.forEach((upsellProduct) => {
            let payloadPart = upsellBasketPayload.product_parts.find((upsellPayloadPart) => {
                return upsellPayloadPart.product_id === upsellProduct.id;
            });

            // Use the part amount from the payload, not the response. If the API call was
            // 'add this combi-upsell with a part amount of 2' and the basket already contained the combi-upsell with
            // 5 of this part, the response would have a part amount of 7. This makes sure we log 2, not 7.
            productData.push({
                amount: payloadPart.amount,
                product: upsellProduct,
            });
        });

        return productData;
    }

    function mapBasketPayloadToGAProductsData(basketPayload: BasketPayload, apiBasketEntries: BasketEntry[]) {
        return basketPayload.entries
            .map((payloadEntry) => {
                // If found in payload, include
                let apiBasketEntry = apiBasketEntries.find((apiBasketEntry) => {
                    return apiBasketEntry.product_id === payloadEntry.product_id;
                });

                if (apiBasketEntry) {
                    return {
                        amount: apiBasketEntry.amount,
                        product: apiBasketEntry.product,
                    };
                } else {
                    return null;
                }
            })
            .filter((mappedEntry) => {
                return mappedEntry !== null;
            });
    }

    function mapCustomerTrackingData(
        altFirstName: string = null,
        altLastName: string = null,
        altEmail: string = null,
        altPhoneNumber: string = null,
    ) {
        if (!customerStore) {
            customerStore = useCustomerStore();
        }

        let customLayer: {
            email?: string;
            phone_social?: string;
            phone_google?: string;
            first_name?: string;
            last_name?: string;
            date_of_birth?: string;
            street?: string;
            city?: string;
            zip?: string;
            country?: string;
            user_id?: number;
        } = {};

        if (customerStore?.customer) {
            customLayer.user_id = customerStore.customer.id;

            if (customerStore.customer.email) {
                customLayer.email = sha256(customerStore.customer.email);
            }

            if (customerStore.customer.sys_telephone) {
                customLayer.phone_social = sha256(customerStore.customer.sys_telephone);
                customLayer.phone_google = sha256(customerStore.customer.sys_telephone.replace(/^\+/, ""));
            }

            if (customerStore.customer.firstname) {
                customLayer.first_name = sha256(customerStore.customer.firstname.toLowerCase());
            }

            if (customerStore.customer.lastname) {
                customLayer.last_name = sha256(customerStore.customer.lastname.toLowerCase());
            }

            if (customerStore.customer.birthday) {
                customLayer.date_of_birth = sha256(
                    DayJs.getInstance()(customerStore.customer.birthday).format("YYYYMMDD"),
                );
            }

            if (customerStore.customer.primaryInvoiceAddress?.street) {
                customLayer.street = sha256(customerStore.customer.primaryInvoiceAddress.street.toLowerCase());
            }

            if (customerStore.customer.primaryInvoiceAddress?.city) {
                customLayer.city = sha256(
                    customerStore.customer.primaryInvoiceAddress.city.replace(/\s+/g, "").toLowerCase(),
                );
            }

            if (customerStore.customer.primaryInvoiceAddress?.postalcode) {
                customLayer.zip = sha256(
                    customerStore.customer.primaryInvoiceAddress.postalcode.replace(/-|\s+/g, "").toLowerCase(),
                );
            }

            if (customerStore.customer.primaryInvoiceAddress?.country_code) {
                customLayer.country = sha256(customerStore.customer.primaryInvoiceAddress.country_code.toLowerCase());
            }
        } else if (altFirstName || altLastName || altEmail || altPhoneNumber) {
            if (altEmail) {
                customLayer.email = sha256(altEmail);
            }

            if (altPhoneNumber) {
                customLayer.phone_social = sha256(altPhoneNumber);
                customLayer.phone_google = sha256(altPhoneNumber.replace(/^\+/, ""));
            }

            if (altFirstName) {
                customLayer.first_name = sha256(altFirstName.toLowerCase());
            }

            if (altLastName) {
                customLayer.last_name = sha256(altLastName.toLowerCase());
            }
        }

        return { customer_data: customLayer };
    }

    function getFormattedPhoneNumber(telephone: string = null): null | string {
        if (!telephone) {
            return null;
        }

        let countryCodeString: string =
            customerStore?.customer?.primaryInvoiceAddress?.country_code ?? window.wordpress_locale.substring(3, 5);
        let countryCode: CountryCode = <CountryCode>countryCodeString.toUpperCase();

        // The parsePhoneNumber method will end code execution without throwing an error when an invalid phonenumber is
        // passed to it, hence the validity check!
        if (isValidPhoneNumber(telephone, countryCode)) {
            return parsePhoneNumber(telephone, countryCode).format("E.164");
        }

        return null;
    }

    function trackGAViewItemList(products: Product[], listName: string, listId: string = listName) {
        let productsData = products.map((product) => {
            return {
                amount: 1,
                product: product,
            };
        });
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = products.map((product, index) => {
            return mapProductForGA(product, listName, index);
        });

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "view_item_list",
                ecommerce: {
                    item_list_name: listName,
                    item_list_id: listId,
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGASelectItemFromList(
        product: Product,
        listName: string,
        listId: string = listName,
        itemIndex: number = null,
    ) {
        let currency = mapProductsDataToCurrency([{ amount: 1, product: product }]);
        let value = mapProductsDataToValue([{ amount: 1, product: product }]);
        let gaProduct = mapProductForGA(product, listName, itemIndex);
        gaProduct.index = itemIndex;
        clearPreviousEcommerceObject();
        window.dataLayer.push({
            event: "select_item",
            ecommerce: {
                item_id: product.id,
                item_list_name: listName,
                item_list_id: listId,
                currency: currency,
                value: value,
                items: [gaProduct],
            },
            ...mapCustomerTrackingData(),
        });
        window.dataLayer.push({});
    }

    function trackGAViewItem(product: Product) {
        let currency = mapProductsDataToCurrency([{ amount: 1, product: product }]);
        let value = mapProductsDataToValue([{ amount: 1, product: product }]);
        let gaProduct = mapProductForGA(product);

        clearPreviousEcommerceObject();

        window.dataLayer.push({
            event: "view_item",
            ecommerce: {
                currency: currency,
                value: value,
                items: [gaProduct],
            },
            ...mapCustomerTrackingData(),
        });
        window.dataLayer.push({});
    }

    function trackGAQuoteRequest(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "quote_request",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAQuoteSubmit(
        productsData: { amount: number; product: Product }[],
        reason: string,
        firstName: string = null,
        lastName: string = null,
        email: string = null,
        telephone: string = null,
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "quote_submit",
                quote_reason: reason,
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(firstName, lastName, email, getFormattedPhoneNumber(telephone ?? "")),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAViewQuote(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "view_quote",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGAAddToCart(productsData: { amount: number; product: Product }[], listName: string = null) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, listName, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "add_to_cart",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAViewCart(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "view_cart",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGARemoveFromCart(productsData: { amount: number; product: Product }[], listName: string = null) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, listName, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "remove_from_cart",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAViewWishlist(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "view_wishlist",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGAAddToWishlist(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "add_to_wishlist",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGARemoveFromWishlist(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "remove_from_wishlist",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGABeginCheckout(productsData: { amount: number; product: Product }[], coupon: string = "") {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "begin_checkout",
                ecommerce: {
                    currency: currency,
                    value: value,
                    coupon: coupon,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAAddUserInfo(productsData: { amount: number; product: Product }[]) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "add_user_info",
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGAAddShippingInfo(
        productsData: { amount: number; product: Product }[],
        shippingTierName: string = null,
        coupon: string = "",
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "add_shipping_info",
                ecommerce: {
                    currency: currency,
                    value: value,
                    coupon: coupon,
                    shipping_tier: shippingTierName,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGAAddPaymentInfo(
        productsData: { amount: number; product: Product }[],
        shippingTierName: string = null,
        paymentMethodName: string = null,
        paymentFee: number = null,
        coupon: string = "",
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (paymentFee) {
            value = (+value + paymentFee).toString();
        }

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "add_payment_info",
                ecommerce: {
                    currency: currency,
                    value: value,
                    shipping_tier: shippingTierName,
                    coupon: coupon,
                    payment_type: paymentMethodName,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGACheckoutSummary(
        productsData: { amount: number; product: Product }[],
        telephone: string,
        email: string,
        shippingTierName: string = null,
        paymentMethodName: string = null,
        paymentFee: number = null,
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (paymentFee) {
            value = (+value + paymentFee).toString();
        }

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "checkout_summary",
                email: email,
                telephone: getFormattedPhoneNumber(telephone),
                ecommerce: {
                    currency: currency,
                    value: value,
                    shipping_tier: shippingTierName,
                    payment_type: paymentMethodName,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGAPurchase(
        productsData: { amount: number; product: Product }[],
        transactionId: string,
        telephone: string,
        email: string,
        shippingTierName: string = null,
        paymentMethodName: string = null,
        shippingFee: number = null,
        paymentFee: number = null,
        coupon: string = "",
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let vat = mapProductsDataToTax(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        if (paymentFee) {
            value = (+value + paymentFee).toString();
        }

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "purchase",
                email: email,
                telephone: getFormattedPhoneNumber(telephone),
                shipping_tier: shippingTierName,
                payment_type: paymentMethodName,
                ecommerce: {
                    currency: currency,
                    transaction_id: transactionId,
                    value: value,
                    coupon: coupon,
                    shipping: shippingFee,
                    tax: vat,
                    items: gaProducts,
                },
                ...mapCustomerTrackingData(),
            });
            window.dataLayer.push({});
        }
    }

    function trackGA404(currentPagePath: string = null) {
        clearPreviousEcommerceObject();

        window.dataLayer.push({
            event: "404",
            page_path: currentPagePath,
        });
        window.dataLayer.push({});
    }

    function trackMenuOpen(menuItemName: string = null) {
        clearPreviousEcommerceObject();

        window.dataLayer.push({
            event: "menu_open",
            menu_item_name: menuItemName,
        });
        window.dataLayer.push({});
    }

    function trackMenuClickCategory(menuItemName: string = null, menuItemUrl: string = null) {
        clearPreviousEcommerceObject();

        window.dataLayer.push({
            event: "menu_click_category",
            menu_item_name: menuItemName,
            menu_item_url: menuItemUrl,
        });
        window.dataLayer.push({});
    }

    function trackCheckoutValidationErrors(
        requestError: string = null,
        productsData: { amount: number; product: Product }[],
        requestErrorfields: object = null,
    ) {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, null, null);

        clearPreviousValidationErrorObject();

        window.dataLayer.push({
            event: "checkout_validation_errors",
            error_message: requestError,
            error_fields: Object.keys(requestErrorfields),
            error_fields_messages: Object.values(requestErrorfields).flat(),
            ecommerce: {
                currency: currency,
                value: value,
                items: gaProducts,
            },
        });
        window.dataLayer.push({});
    }

    function trackAnchorNavigationClickItem(navigationItemName: string = null) {
        clearPreviousEcommerceObject();

        window.dataLayer.push({
            event: "anchor_navigation_click",
            navigation_item_name: navigationItemName,
        });
        window.dataLayer.push({});
    }

    function trackWoodTableAddToCart(productsData: { amount: number; product: Product }[], composition: string): void {
        let currency = mapProductsDataToCurrency(productsData);
        let value = mapProductsDataToValue(productsData);
        let gaProducts = mapProductsForGA(productsData, "wood table", null);

        if (gaProducts && gaProducts.length > 0) {
            clearPreviousEcommerceObject();

            window.dataLayer.push({
                event: "wood_table_add_to_cart",
                composition: composition,
                ecommerce: {
                    currency: currency,
                    value: value,
                    items: gaProducts,
                },
            });
            window.dataLayer.push({});
        }
    }

    function trackGALogin() {
        window.dataLayer.push({
            event: "login",
            ...mapCustomerTrackingData(),
        });

        window.dataLayer.push({});
    }

    function trackGASearch(searchTerm: string): void {
        window.dataLayer.push({
            event: "search",
            search_term: searchTerm,
            ...mapCustomerTrackingData(),
        });

        window.dataLayer.push({});
    }

    function trackGAShare(method: string, contentType?: string | null, itemId?: number | null): void {
        window.dataLayer.push({
            event: "share",
            method: method,
            content_type: contentType,
            item_id: itemId,
            ...mapCustomerTrackingData(),
        });

        window.dataLayer.push({});
    }

    function trackGASignUp(): void {
        window.dataLayer.push({
            event: "sign_up",
            ...mapCustomerTrackingData(),
        });

        window.dataLayer.push({});
    }

    function trackGAPageView(): void {
        window.dataLayer.push({
            event: "page_view",
            ...mapCustomerTrackingData(),
        });

        window.dataLayer.push({});
    }

    function trackVariationDropDownSelection(isDifferentVariationChosen: boolean) {
        window.dataLayer.push({
            event: "click",
            is_different_variation: isDifferentVariationChosen,
        });
        window.dataLayer.push({});
    }

    return {
        trackAnchorNavigationClickItem,
        mapOrderToGAProductsData,
        mapBasketEntriesToGAProductsData,
        mapBasketPayloadToGAProductsData,
        mapCombiUpsellBasketPayloadToGAProductsData,
        trackGAPageView,
        trackGAViewItemList,
        trackGASelectItemFromList,
        trackGAQuoteRequest,
        trackGAQuoteSubmit,
        trackGAViewQuote,
        trackGAAddToCart,
        trackGAViewCart,
        trackGARemoveFromCart,
        trackGAViewWishlist,
        trackGAAddToWishlist,
        trackGARemoveFromWishlist,
        trackGABeginCheckout,
        trackGAAddUserInfo,
        trackGAAddShippingInfo,
        trackGAAddPaymentInfo,
        trackGACheckoutSummary,
        trackGAPurchase,
        trackGAViewItem,
        trackGA404,
        trackGALogin,
        trackGASearch,
        trackGAShare,
        trackGASignUp,
        trackMenuOpen,
        trackMenuClickCategory,
        trackCheckoutValidationErrors,
        trackWoodTableAddToCart,
        trackVariationDropDownSelection,
    };
}
